import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  SkipBack,
  SkipForward,
  Settings,
  ListVideo,
  ArrowRight,
  Lock,
  CheckCircle2,
  Sparkles,
  Loader2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper: convert a video share URL into an embeddable iframe URL
// Supports: YouTube (preferred), Google Drive (fallback), direct .mp4
const resolveSource = (url) => {
  if (!url) return { type: "mp4", src: "" };

  // YouTube — matches youtu.be/ID and youtube.com/watch?v=ID
  const ytMatch =
    url.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/) ||
    url.match(/youtube\.com\/watch\?[^#]*v=([a-zA-Z0-9_-]{6,})/) ||
    url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/) ||
    url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
  if (ytMatch) {
    const id = ytMatch[1];
    const params = "rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&fs=1&color=white";
    return { type: "youtube", src: `https://www.youtube-nocookie.com/embed/${id}?${params}` };
  }

  // Google Drive — file/d/ID/view or open?id=ID
  const driveMatch =
    url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/) ||
    url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/) ||
    url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    return { type: "drive", src: `https://drive.google.com/file/d/${driveMatch[1]}/preview` };
  }

  return { type: "mp4", src: url };
};

const SESSIONS = [
  {
    id: 1,
    title: "الجلسة الأولى",
    description: "تسجيل الجلسة الأولى من كورس وعي الغذاء صلاة.",
    src: "https://drive.google.com/file/d/1Zyzp5pG4QWasPCNz1JAfveVDRzIBEi8s/view?usp=sharing",
  },
  {
    id: 2,
    title: "الجلسة الثانية",
    description: "تسجيل الجلسة الثانية من كورس وعي الغذاء صلاة.",
    src: "https://drive.google.com/file/d/1Y5D-XRJ7Ln4-Y1fsgfyWcZjsVI-O-FeD/view?usp=sharing",
  },
  {
    id: 3,
    title: "الجلسة الثالثة",
    description: "تسجيل الجلسة الثالثة من كورس وعي الغذاء صلاة.",
    src: "https://drive.google.com/file/d/1MFgSuApY-pnZPrkbo2i6alxD6b0RD_JW/view?usp=drive_link",
  },
  {
    id: 4,
    title: "الجلسة الرابعة",
    description: "تسجيل الجلسة الرابعة من كورس وعي الغذاء صلاة.",
    src: "https://drive.google.com/file/d/1cvrqGigqf95jzBamtzOPS62R-m8-u3J3/view?usp=drive_link",
  },
];

const formatTime = (sec) => {
  if (!Number.isFinite(sec)) return "00:00";
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(r)}` : `${pad(m)}:${pad(r)}`;
};

const VideoPlayer = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimerRef = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null); // { granted, days_remaining, access_expires_at }
  const [accessChecking, setAccessChecking] = useState(true);

  const current = SESSIONS[currentIndex];
  const resolved = resolveSource(current.src);
  const isDrive = resolved.type === "drive";
  const isYoutube = resolved.type === "youtube";
  const isEmpty = !current.src;

  // ----- Access guard: backend-verified 1-year window -----
  useEffect(() => {
    if (loading) return;
    if (!user) {
      toast.error("يرجى تسجيل الدخول لمشاهدة الجلسات المُسجَّلة");
      navigate("/", { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/watch/access`, { withCredentials: true });
        if (cancelled) return;
        setAccessInfo(res.data);
      } catch (err) {
        if (cancelled) return;
        const reason = err?.response?.data?.detail;
        if (reason === "access_expired") {
          toast.error("انتهت مدة وصولك إلى الكورس (سنة كاملة). يرجى التواصل مع الإدارة لتجديد الاشتراك.", { duration: 6000 });
        } else if (reason === "not_enrolled" || reason === "no_payment_record") {
          toast.error("هذه الصفحة مخصصة للمشاركين المسجَّلين في الكورس فقط");
        } else {
          toast.error("تعذّر التحقق من صلاحية الوصول");
        }
        navigate("/", { replace: true });
      } finally {
        if (!cancelled) setAccessChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  // ----- Fullscreen tracking -----
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // ----- Auto-hide controls -----
  const bumpControls = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2800);
  }, []);

  useEffect(() => {
    bumpControls();
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, [bumpControls, currentIndex, isPlaying]);

  // ----- Video event handlers -----
  const handlePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.buffered.length > 0) {
      setBuffered(v.buffered.end(v.buffered.length - 1));
    }
  };

  const handleLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // RTL-aware: invert x because dir="rtl" doesn't flip getBoundingClientRect
    const x = e.clientX - rect.left;
    const ratio = Math.min(1, Math.max(0, x / rect.width));
    v.currentTime = ratio * duration;
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
    }
    setIsMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const skip = (delta) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(duration || 0, Math.max(0, v.currentTime + delta));
  };

  const selectSession = (idx) => {
    setCurrentIndex(idx);
    setCurrentTime(0);
    setBuffered(0);
    setShowSettings(false);
    setIsBuffering(true);
  };

  const goNext = () => {
    if (currentIndex < SESSIONS.length - 1) selectSession(currentIndex + 1);
  };
  const goPrev = () => {
    if (currentIndex > 0) selectSession(currentIndex - 1);
  };

  const setRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setShowSettings(false);
  };

  // ----- Keyboard shortcuts -----
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.code === "Space") { e.preventDefault(); handlePlayPause(); }
      else if (e.code === "ArrowRight") skip(-10); // RTL: right = back
      else if (e.code === "ArrowLeft") skip(10);   // RTL: left = forward
      else if (e.code === "KeyM") toggleMute();
      else if (e.code === "KeyF") toggleFullscreen();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [duration, isMuted]);

  // Prevent right-click on video
  const blockContextMenu = (e) => e.preventDefault();

  if (loading || !user || accessChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F]">
        <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
      </div>
    );
  }

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration > 0 ? (buffered / duration) * 100 : 0;
  const watermarkLabel = user?.email || user?.name || "Enrolled";

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#05050A] via-[#0A0A14] to-[#0F0A1A] text-white"
      style={{ fontFamily: "Cairo, sans-serif" }}
      data-testid="video-player-page"
    >
      {/* Decorative background glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden -z-0">
        <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-amber-500/10 blur-[140px]"></div>
        <div className="absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full bg-purple-600/10 blur-[140px]"></div>
      </div>

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between gap-2 px-3 sm:px-4 md:px-8 py-3 md:py-4 border-b border-white/5 backdrop-blur-sm bg-black/30">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 md:px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-xs sm:text-sm md:text-base flex-shrink-0"
          data-testid="watch-back-button"
        >
          <ArrowRight className="w-4 h-4" />
          <span className="hidden xs:inline sm:hidden">الرئيسية</span>
          <span className="hidden sm:inline">العودة للرئيسية</span>
        </button>

        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 flex-shrink min-w-0">
          {accessInfo && accessInfo.days_remaining !== null && accessInfo.days_remaining !== undefined && (
            <div
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full border flex-shrink-0 ${
                accessInfo.days_remaining <= 30
                  ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                  : "bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
              }`}
              data-testid="watch-days-remaining"
              title={accessInfo.access_expires_at ? `حتى ${new Date(accessInfo.access_expires_at).toLocaleDateString('ar')}` : ""}
            >
              <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span className="text-[11px] sm:text-xs md:text-sm font-bold whitespace-nowrap">
                <span className="hidden sm:inline">متبقّي </span>{accessInfo.days_remaining} يوم
              </span>
            </div>
          )}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-amber-300/10 border border-amber-400/30 flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="text-xs md:text-sm font-semibold text-amber-200 whitespace-nowrap">الكورس المُسجَّل</span>
          </div>
          <button
            onClick={() => setShowPlaylist((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex-shrink-0"
            data-testid="watch-toggle-playlist"
            aria-label="إظهار/إخفاء قائمة التشغيل"
          >
            <ListVideo className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </header>

      <main className="relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 md:gap-6 p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1500px] mx-auto">
        {/* Player */}
        <section>
          <div
            ref={containerRef}
            className="relative group rounded-2xl overflow-hidden bg-black ring-1 ring-white/10 shadow-2xl shadow-amber-500/5"
            onMouseMove={bumpControls}
            onMouseLeave={() => isPlaying && setShowControls(false)}
            onContextMenu={blockContextMenu}
            data-testid="video-player-container"
          >
            {isEmpty ? (
              <div className="w-full aspect-video bg-black flex flex-col items-center justify-center text-center px-4 sm:px-6" data-testid="video-empty-placeholder">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center mb-3 sm:mb-4">
                  <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-amber-300" />
                </div>
                <h3 className="text-base sm:text-lg md:text-2xl font-bold text-white">قريبًا — التسجيلة قيد التحضير</h3>
                <p className="text-white/60 mt-2 text-xs sm:text-sm md:text-base max-w-md leading-relaxed">
                  لم يتم تسجيل هذه الجلسة بعد. ستُضاف هنا فور انتهاء البثّ المباشر.
                </p>
              </div>
            ) : isYoutube ? (
              <div className="relative w-full aspect-video bg-black overflow-hidden">
                <iframe
                  key={resolved.src}
                  src={resolved.src}
                  title={current.title}
                  className="absolute inset-0 w-full h-full bg-black"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  data-testid="video-iframe-youtube"
                />
              </div>
            ) : isDrive ? (
              <div className="relative w-full aspect-video bg-black overflow-hidden">
                <iframe
                  key={resolved.src}
                  src={resolved.src}
                  title={current.title}
                  className="absolute inset-0 w-full h-full bg-black"
                  allow="autoplay; encrypted-media"
                  referrerPolicy="no-referrer"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  data-testid="video-iframe"
                />
                {/* Custom fullscreen button — fullscreens the CONTAINER so watermark stays */}
                <button
                  onClick={toggleFullscreen}
                  className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-30 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm border border-white/15 text-white flex items-center justify-center transition-all"
                  data-testid="drive-fullscreen-button"
                  aria-label="ملء الشاشة"
                  type="button"
                >
                  {isFullscreen ? <Minimize className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize className="w-4 h-4 sm:w-5 sm:h-5" />}
                </button>
              </div>
            ) : (
              <video
                ref={videoRef}
                key={resolved.src}
                src={resolved.src}
                className="w-full aspect-video bg-black"
                onClick={handlePlayPause}
                onPlay={() => { setIsPlaying(true); setIsBuffering(false); }}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onWaiting={() => setIsBuffering(true)}
                onPlaying={() => setIsBuffering(false)}
                onCanPlay={() => setIsBuffering(false)}
                onEnded={goNext}
                onContextMenu={blockContextMenu}
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                playsInline
                data-testid="video-element"
              />
            )}
            {!isEmpty && (
            <div
              className="absolute top-3 left-3 md:top-4 md:left-4 pointer-events-none select-none text-white/35 text-[11px] md:text-sm font-semibold tracking-wider"
              style={{
                textShadow: "0 1px 4px rgba(0,0,0,0.7)",
              }}
              data-testid="video-watermark"
            >
              {watermarkLabel} • وعي الغذاء صلاة
            </div>
            )}

            {/* Buffering spinner */}
            {!isDrive && !isEmpty && isBuffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
              </div>
            )}

            {/* Center play overlay when paused */}
            {!isDrive && !isEmpty && !isPlaying && !isBuffering && (
              <button
                onClick={handlePlayPause}
                className="absolute inset-0 flex items-center justify-center group/center"
                data-testid="center-play-button"
                  aria-label="تشغيل"
              >
                <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/40"></span>
                <span className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-2xl shadow-amber-500/40 ring-4 ring-white/20 group-hover/center:scale-110 transition-transform">
                  <Play className="w-10 h-10 md:w-12 md:h-12 text-black ml-1" fill="currentColor" />
                </span>
              </button>
            )}

            {/* Custom controls — hidden for Drive iframe (uses Google's own controls) and empty sessions */}
            {!isDrive && !isEmpty && (
            <div
              className={`absolute left-0 right-0 bottom-0 px-3 md:px-5 pb-3 md:pb-4 pt-10 bg-gradient-to-t from-black/95 via-black/70 to-transparent transition-opacity duration-300 ${
                showControls || !isPlaying ? "opacity-100" : "opacity-0"
              }`}
              dir="ltr"
            >
              {/* Progress bar */}
              <div
                className="relative h-1.5 md:h-2 w-full bg-white/15 rounded-full cursor-pointer group/bar mb-3"
                onClick={handleSeek}
                data-testid="video-progress-bar"
              >
                <div
                  className="absolute inset-y-0 left-0 bg-white/25 rounded-full"
                  style={{ width: `${bufferedPercent}%` }}
                ></div>
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-300 rounded-full"
                  style={{ width: `${progressPercent}%` }}
                ></div>
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 md:w-4 md:h-4 rounded-full bg-amber-300 shadow-lg shadow-amber-500/50 opacity-0 group-hover/bar:opacity-100 transition-opacity"
                  style={{ left: `calc(${progressPercent}% - 8px)` }}
                ></div>
              </div>

              <div className="flex items-center justify-between gap-2 md:gap-3">
                <div className="flex items-center gap-1.5 md:gap-3">
                  <button
                    onClick={handlePlayPause}
                    className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                    data-testid="play-pause-button"
                    aria-label={isPlaying ? "إيقاف" : "تشغيل"}
                  >
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" fill="currentColor" />}
                  </button>

                  <button
                    onClick={goPrev}
                    disabled={currentIndex === 0}
                    className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="prev-session-button"
                    aria-label="الجلسة السابقة"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    onClick={goNext}
                    disabled={currentIndex === SESSIONS.length - 1}
                    className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid="next-session-button"
                    aria-label="الجلسة التالية"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>

                  <div className="hidden sm:flex items-center gap-2 ml-1">
                    <button
                      onClick={toggleMute}
                      className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                      data-testid="mute-button"
                      aria-label="كتم الصوت"
                    >
                      {isMuted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-20 accent-amber-400 cursor-pointer"
                      data-testid="volume-slider"
                      aria-label="مستوى الصوت"
                    />
                  </div>

                  <span className="text-xs md:text-sm tabular-nums text-white/80 ml-1">
                    {formatTime(currentTime)} <span className="text-white/40">/</span> {formatTime(duration)}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 md:gap-2 relative">
                  <button
                    onClick={() => setShowSettings((v) => !v)}
                    className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                    data-testid="settings-button"
                    aria-label="الإعدادات"
                  >
                    <Settings className="w-4 h-4" />
                  </button>

                  {showSettings && (
                    <div className="absolute bottom-12 right-0 w-44 rounded-xl bg-[#0F0F18]/95 backdrop-blur border border-white/10 shadow-2xl p-2 z-30" data-testid="settings-menu">
                      <p className="px-2 py-1.5 text-[11px] uppercase tracking-wider text-white/50">سرعة التشغيل</p>
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
                        <button
                          key={r}
                          onClick={() => setRate(r)}
                          className={`w-full text-left px-3 py-1.5 rounded-md text-sm hover:bg-white/10 flex items-center justify-between ${
                            playbackRate === r ? "text-amber-300" : "text-white/85"
                          }`}
                          data-testid={`speed-${r}`}
                        >
                          <span>{r === 1 ? "عادي" : `${r}x`}</span>
                          {playbackRate === r && <CheckCircle2 className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={toggleFullscreen}
                    className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                    data-testid="fullscreen-button"
                    aria-label="ملء الشاشة"
                  >
                    {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Now-playing info */}
          <div className="mt-4 sm:mt-5 md:mt-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-200 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider">
                يُشغَّل الآن
              </span>
              <span className="text-white/50 text-xs">
                {currentIndex + 1} / {SESSIONS.length}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white leading-tight">
              {current.title}
            </h1>
            <p className="text-white/60 mt-2 text-sm md:text-base leading-relaxed">{current.description}</p>

            <div className="mt-3 sm:mt-4 flex items-start sm:items-center gap-2 text-[11px] sm:text-xs text-white/40 leading-snug">
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span>محتوى محمي — للمشاركين المسجَّلين في الكورس فقط.</span>
            </div>
          </div>
        </section>

        {/* Playlist */}
        <aside
          className={`${showPlaylist ? "block" : "hidden"} lg:block`}
          data-testid="playlist-aside"
        >
          <div className="lg:sticky lg:top-4 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm overflow-hidden">
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListVideo className="w-5 h-5 text-amber-300" />
                <h2 className="font-bold text-sm sm:text-base">قائمة التشغيل</h2>
              </div>
              <span className="text-xs text-white/50">{SESSIONS.length} جلسات</span>
            </div>

            <ul className="divide-y divide-white/5" data-testid="playlist-items">
              {SESSIONS.map((s, idx) => {
                const active = idx === currentIndex;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => selectSession(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-right transition-all ${
                        active
                          ? "bg-gradient-to-r from-amber-500/15 to-transparent"
                          : "hover:bg-white/5"
                      }`}
                      data-testid={`playlist-item-${idx}`}
                    >
                      {/* Thumb */}
                      <div className={`relative w-20 h-12 md:w-24 md:h-14 rounded-lg flex-shrink-0 overflow-hidden ring-1 ${active ? "ring-amber-400/60" : "ring-white/10"}`}>
                        <div className={`absolute inset-0 bg-gradient-to-br ${
                          idx === 0 ? "from-amber-500/40 to-orange-700/40" :
                          idx === 1 ? "from-purple-500/40 to-fuchsia-700/40" :
                          idx === 2 ? "from-cyan-500/40 to-blue-700/40" :
                                       "from-emerald-500/40 to-teal-700/40"
                        }`}></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          {active && isPlaying ? (
                            <span className="flex items-end gap-0.5 h-5">
                              <span className="w-1 bg-amber-300 animate-pulse" style={{ height: "60%" }}></span>
                              <span className="w-1 bg-amber-300 animate-pulse" style={{ height: "100%", animationDelay: "0.15s" }}></span>
                              <span className="w-1 bg-amber-300 animate-pulse" style={{ height: "75%", animationDelay: "0.3s" }}></span>
                            </span>
                          ) : (
                            <Play className="w-5 h-5 text-white/90" fill="currentColor" />
                          )}
                        </div>
                        <span className="absolute bottom-1 right-1 text-[10px] font-bold text-white/90 bg-black/50 rounded px-1">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm md:text-base font-bold truncate ${active ? "text-amber-200" : "text-white/90"}`}>
                          {s.title}
                        </p>
                        <p className="text-xs text-white/50 truncate">الجلسة {String(idx + 1).padStart(2, "0")}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="hidden lg:block px-5 py-3 border-t border-white/10 text-[11px] text-white/40 leading-relaxed">
              اختصارات: <span className="text-white/60">مسافة</span> تشغيل/إيقاف • <span className="text-white/60">F</span> ملء الشاشة • <span className="text-white/60">M</span> كتم الصوت
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default VideoPlayer;
